import { rotatedX, rotatedY } from '../math/MathUtils';
import { Vector2 } from '../math/Vector2';
import { type Transform } from './Transform';

/**
 * An entity's absolute placement in the world, after every parent transform has been applied.
 *
 * Produced on demand rather than stored, so nothing can go stale. Callers pass an instance in to be
 * filled, which keeps the render loop free of per-frame allocations.
 */
export class WorldTransform {
  /** Absolute position in world units. */
  readonly position = Vector2.zero();
  /** Absolute rotation in radians. */
  rotation = 0;
  /** Absolute scale. */
  readonly scale = Vector2.one();

  /** Fills this world transform from a transform that has no parent. */
  copyFromLocal(local: Transform): this {
    this.position.copyFrom(local.position);
    this.rotation = local.rotation;
    this.scale.copyFrom(local.scale);
    return this;
  }
}

/**
 * Applies a parent's world transform to a local transform.
 *
 * Reads every input before writing, so passing the same instance as `parent` and `out` is safe and is
 * how the parent chain is folded in place.
 */
export function composeWorldTransform(parent: WorldTransform, local: Transform, out: WorldTransform): void {
  const parentX = parent.position.x;
  const parentY = parent.position.y;
  const parentScaleX = parent.scale.x;
  const parentScaleY = parent.scale.y;
  const cos = Math.cos(parent.rotation);
  const sin = Math.sin(parent.rotation);

  const scaledX = local.position.x * parentScaleX;
  const scaledY = local.position.y * parentScaleY;

  out.position.set(parentX + rotatedX(scaledX, scaledY, cos, sin), parentY + rotatedY(scaledX, scaledY, cos, sin));
  out.rotation = parent.rotation + local.rotation;
  out.scale.set(parentScaleX * local.scale.x, parentScaleY * local.scale.y);
}
